import {
  generateFlowFormCode,
  getFlowHistoryByIns,
  getNowNodeByInsId,
  passFlow,
  startFlow,
} from "@/apis/flow.api";
import { ProcessTimelineItem } from "@/components/SelfProcessTimeline";
import { FLOW_STATUS, type FlowCode } from "@/constants";
import { PassFlowSchema, StartFlowSchema } from "@/types/apis";
import { useMutation, useQuery } from "@tanstack/react-query";

export function useFlow(flowCode: FlowCode, enabled: boolean) {
  const result = useQuery({
    queryKey: ["generateFlowFormCode", flowCode],
    queryFn: () => generateFlowFormCode(flowCode),
    enabled,
  });

  const startFlowMutation = useMutation({
    mutationFn: (data: StartFlowSchema) => startFlow(data),
  });

  const passFlowMutation = useMutation({
    mutationFn: (data: PassFlowSchema) =>
      passFlow(data.taskId, data.flowParamsDto),
  });

  return {
    flowCode: result.data?.data.data,
    startFlowMutation,
    passFlowMutation,
    mutationLoading: startFlowMutation.isPending || passFlowMutation.isPending,
  };
}

function useFlowHistory(insId?: number) {
  return useQuery({
    queryKey: ["flowHistoryQuery", insId],
    queryFn: () => getFlowHistoryByIns(insId!),
    enabled: !!insId,
  });
}

export function useFlowNodeQuery(insId?: number) {
  const result = useFlowHistory(insId);
  const nodes = result.data?.data.data || [];

  const timeLineItems: ProcessTimelineItem[] = nodes.map((node, index) => {
    let status: ProcessTimelineItem["status"] = "pending";

    if (node.flowStatus === FLOW_STATUS.APPROVING) {
      status = "pending";
    } else if (
      node.flowStatus === FLOW_STATUS.COMPLETED ||
      node.flowStatus === FLOW_STATUS.APPROVED
    ) {
      status = "pass";
    } else {
      status = "rejected";
    }

    return {
      id: index.toString(),
      name: node.approver,
      role: node.postName,
      status: index === 0 ? "start" : (status as ProcessTimelineItem["status"]),
      timestamp: node.createTime,
      comment: node.message,
    };
  });

  return timeLineItems;
}

export function useNowFlowNode(insId?: number) {
  const result = useQuery({
    queryKey: ["nowFlowNode", insId],
    queryFn: () => getNowNodeByInsId(insId!),
    enabled: !!insId,
  });

  const currentNode = result.data?.data.data[0];

  if (!currentNode) return;

  return currentNode.nodeCode;
}

/**
 * 是否存在指定节点的历史记录
 */
export function useExistFlowHistory(nodeCodes: string[], insId?: number) {
  const result = useFlowHistory(insId);

  if (!insId || nodeCodes.length === 0) return false;

  const list = result.data?.data.data || [];

  return list.some((item) => nodeCodes.includes(item.nodeCode));
}
